"use client";

import React, { useState } from "react";
import styled from "styled-components";
import { Text, Dialog } from "@lmn/design-system";
import IconSort from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSort.js";
import IconCheck from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconCheck.js";
import { SortOptionType } from "@/lib/Types/InspirationSortFilter";
import { useSortLogic } from "@/lib/Hooks/useSortLogic";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";

const Button = styled.button`
  flex: 1 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  height: 100%;
  min-width: 0;
  position: relative;

  &:hover {
    background: ${(props) =>
    props.theme.kallisto.color.background.interactive.backgroundNeutralHover};
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 38px;
    background: ${(props) =>
    props.theme.kallisto.color.background.interactive.backgroundNeutralHover};
  }
`;

const ButtonText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  flex: 1;
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  color: ${(props) => props.theme.kallisto.color.text.static.textPrimary};
`;

const RadioOption = styled.div`
  cursor: pointer;
  border-bottom: 1px solid
    ${(props) =>
    props.theme.kallisto.color.background.interactive.backgroundNeutralHover};

  &:last-child {
    border-bottom: none;
  }
`;

const RadioContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${(props) =>
    props.theme.kallisto.color.background.static.backgroundPage};

  &:hover {
    background: ${(props) =>
    props.theme.kallisto.color.background.interactive.backgroundNeutralHover};
  }
`;

const StyledIconCheck = styled(IconCheck)`
  color: ${(props) =>
    props.theme.kallisto.color.icon.interactive.iconPrimaryDefault};
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) =>
    props.theme.kallisto.color.background.static.backgroundPage};
  border-radius: 8px;
  margin: 1rem;
  overflow: hidden;
`;

const StyledTextNormal = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  text-align: center;
`;

type SortBarMobileProps = {
  sortOptions: SortOptionType[];
  defaultSortOption: SortOptionType;
  envConfig: EnvConfig;
};

const SortBarMobile: React.FC<SortBarMobileProps> = ({
  sortOptions,
  defaultSortOption,
  envConfig,
}) => {
  const [isSortDialogOpen, setIsSortDialogOpen] = useState(false);
  const { selectedSort, setSelectedSort, currentOption } = useSortLogic(
    sortOptions,
    defaultSortOption,
    envConfig
  );

  return (
    <>
      <Button
        data-testid="sortButtonMobile"
        onClick={() => setIsSortDialogOpen(true)}
      >
        <ButtonContent>
          <IconSort />
          <ButtonText>
            <Text variant="BodySmall">Sortieren</Text>
            <StyledTextNormal variant="BodySmall" color="textSecondary">
              {currentOption?.label}
            </StyledTextNormal>
          </ButtonText>
        </ButtonContent>
      </Button>

      <Dialog
        isOpen={isSortDialogOpen}
        onClose={() => setIsSortDialogOpen(false)}
        header={{ title: "Sortieren", sticky: true }}
        bodyBackgroundColor={"backgroundLayer01"}
      >
        <OptionsContainer>
          {sortOptions.map((option) => (
            <RadioOption
              key={option.value}
              onClick={() => {
                setSelectedSort(option.value);
                setIsSortDialogOpen(false);
              }}
              data-testid={`mobileSort-${option.value}`}
              data-testid-active={selectedSort === option.value}
            >
              <RadioContent>
                <Text variant="BodySmall">{option.label}</Text>
                {selectedSort === option.value && <StyledIconCheck />}
              </RadioContent>
            </RadioOption>
          ))}
        </OptionsContainer>
      </Dialog>
    </>
  );
};

export default SortBarMobile;
